import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, Ticket, ChevronRight, Check, Tag, X, FileText, Building2, Tent, Music, Mic, Coffee, Theater, Sparkles } from "lucide-react";

const ROOM_ICONS = { Building2, Calendar, MapPin, Tent, Music, Mic, Coffee, Theater, Sparkles };
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

import { API_URL } from "../config";
import { sanitizeHtml } from "../lib/sanitize";
import { suggestEmailFix } from "../lib/emailTypo";
import { AccountHeaderWidget } from "../components/user/AccountHeaderWidget";
import { ParentalAuthSection } from "../components/ParentalAuthSection";
import { useUserAuth } from "../context/UserAuthContext";
import { InactiveLandingView } from "./InactiveLandingView";

// Helper to convert hex to rgba
const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Format ISO date (YYYY-MM-DD) to long Portuguese form. Falls back to raw value for non-ISO strings.
const formatSessionDate = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return value;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" });
};

// Resolve image URL: use proxy for Drive/Box links that need it
const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.includes("drive.google.com/uc?")) return `${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
  if (url.includes("app.box.com")) return `${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
  return url;
};

const LandingPage = () => {
  const { user: loggedInUser } = useUserAuth();
  const [show, setShow] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("__ALL__"); // "__ALL__" | room.id | "__NONE__"
  const [selectedSession, setSelectedSession] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [parentalPayload, setParentalPayload] = useState(null);
  const [parentalValid, setParentalValid] = useState(false);
  const handleParentalChange = React.useCallback((payload, valid) => {
    setParentalPayload(payload);
    setParentalValid(valid);
  }, []);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [paymentMode, setPaymentMode] = useState("full");           // "full" | "installment"
  const [paymentModeTouched, setPaymentModeTouched] = useState(false); // becomes true when user explicitly picks an option
  const [installmentSurchargeAccepted, setInstallmentSurchargeAccepted] = useState(false); // checkbox confirming surcharge awareness
  const [firstInstallment, setFirstInstallment] = useState("");     // string for input UX
  const [selectedPlanShortcut, setSelectedPlanShortcut] = useState(null);  // null | 2 | 3 | ...
  const [customFields, setCustomFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Fix mobile "black screen" bug: on Android/iOS the user is scrolled far
  // down (past the parental section) when they press Continuar. When the
  // form is replaced by the smaller success card the browser keeps the old
  // scroll position and the user only sees the empty page background. Scroll
  // to the top of the tickets section so the success card is always visible.
  useEffect(() => {
    if (!registrationComplete) return;
    const el = document.getElementById("tickets") || document.querySelector('[data-testid="registration-success"]');
    if (el && typeof el.scrollIntoView === "function") {
      // Give React one paint to render the success card before scrolling
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [registrationComplete]);
  const [registrationData, setRegistrationData] = useState(null);
  const [showOfflineOptions, setShowOfflineOptions] = useState(false);
  const [offlineSelected, setOfflineSelected] = useState(null);
  const [showStripeConfirmModal, setShowStripeConfirmModal] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState({}); // { fieldName: { original, suggestion } }
  const [otherSelected, setOtherSelected] = useState({}); // { fieldName: bool } — dropdown "Outro" gate
  const [showAuthGate, setShowAuthGate] = useState(false);
  const navigate = useNavigate();

  // ── Dynamic parental authorisation trigger ────────────────────────────
  // The form is only displayed if (a) the ticket type requires parental auth
  // AND (b) the buyer's entered age falls into one of the configured
  // `parental_categories`. This lets a single ticket cover a wide age range
  // (e.g. 8–80 years) while only obliging minors to sign the term.
  const allowedParentalCategories = React.useMemo(() => {
    if (!selectedTicketType?.requires_parental_auth) return [];
    if (selectedTicketType.parental_categories && selectedTicketType.parental_categories.length) {
      return selectedTicketType.parental_categories;
    }
    // Back-compat: if no list, fall back to legacy single age_category.
    if (selectedTicketType.age_category && selectedTicketType.age_category !== "adult") {
      return [selectedTicketType.age_category];
    }
    return [];
  }, [selectedTicketType]);

  const hasAgeField = React.useMemo(
    () => customFields.some((f) => f.name === "age" || f.name === "idade"),
    [customFields]
  );

  const buyerAgeCategory = React.useMemo(() => {
    const raw = formData.age ?? formData.idade;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n < 13) return "child_under_12";
    if (n < 18) return "teen_13_17";
    return "adult";
  }, [formData.age, formData.idade]);

  const parentalRequired = React.useMemo(() => {
    if (!selectedTicketType?.requires_parental_auth) return false;
    if (!allowedParentalCategories.length) return true; // legacy fallback
    // If checkout has no age field at all, default to ALWAYS requiring (safer).
    if (!hasAgeField) return true;
    // No age entered yet → don't pre-show the heavy form. Submit-time guard
    // below will still ask the buyer to fill the age field first.
    if (!buyerAgeCategory) return false;
    return allowedParentalCategories.includes(buyerAgeCategory);
  }, [selectedTicketType, allowedParentalCategories, hasAgeField, buyerAgeCategory]);

  useEffect(() => {
    fetchData();
    // Track page visit
    axios.post(`${API_URL}/api/track-visit`).catch(() => {});

    // Pre-fill coupon from URL (?cupom=XTREMO-XXXX). The email part of the
    // gift link is handled inside fetchData() because it depends on customFields.
    try {
      const params = new URLSearchParams(window.location.search);
      const cupom = params.get("cupom") || params.get("coupon");
      if (cupom) setCouponCode(cupom.toUpperCase());
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (selectedSession) {
      fetchTicketTypes(selectedSession.id);
    }
  }, [selectedSession]);

  // Prefill checkout form when the logged-in user becomes available (the
  // /me request usually resolves a few ticks after the page mounts).
  useEffect(() => {
    if (!loggedInUser) return;
    setFormData((prev) => {
      // Only prefill fields the user hasn't typed into yet (empty)
      const prefill = {
        first_name: loggedInUser.first_name || "",
        last_name: loggedInUser.last_name || "",
        email: loggedInUser.email || "",
        phone: loggedInUser.phone || "",
      };
      const next = { ...prev };
      Object.entries(prefill).forEach(([k, v]) => {
        if (v && !next[k]) next[k] = v;
      });
      return next;
    });
  }, [loggedInUser]);

  const fetchData = async () => {
    try {
      const [showRes, sessionsRes, fieldsRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/show`),
        axios.get(`${API_URL}/api/sessions`),
        axios.get(`${API_URL}/api/custom-fields`),
        axios.get(`${API_URL}/api/rooms`).catch(() => ({ data: [] })),
      ]);
      setShow(showRes.data);
      setSessions(sessionsRes.data);
      setCustomFields(fieldsRes.data);
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);

      // Initialize form data with empty values for each field, but PRESERVE
      // any URL-prefilled values (e.g. ?email=foo@hotmial.com) set on mount.
      const params = new URLSearchParams(window.location.search);
      const emailFromLink = (params.get("email") || "").toLowerCase();

      const initialFormData = {};
      const initialSuggestions = {};
      fieldsRes.data.forEach((field) => {
        if (field.field_type === "checkbox") {
          initialFormData[field.name] = false;
        } else if (field.field_type === "email" && emailFromLink) {
          initialFormData[field.name] = emailFromLink;
          const s = suggestEmailFix(emailFromLink);
          if (s) initialSuggestions[field.name] = s;
        } else {
          initialFormData[field.name] = "";
        }
      });

      // If the buyer is logged in, prefill the standard fields (but don't
      // overwrite URL-passed values). Each ticket can still be edited freely
      // because 1 account can buy bilhetes para várias pessoas com dados
      // distintos.
      if (loggedInUser) {
        const acctPrefill = {
          first_name: loggedInUser.first_name || "",
          last_name: loggedInUser.last_name || "",
          email: loggedInUser.email || "",
          phone: loggedInUser.phone || "",
        };
        Object.entries(acctPrefill).forEach(([k, v]) => {
          if (v && !initialFormData[k]) initialFormData[k] = v;
        });
      }
      setFormData(initialFormData);
      if (Object.keys(initialSuggestions).length > 0) {
        setEmailSuggestions(initialSuggestions);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketTypes = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/ticket-types/${sessionId}`);
      setTicketTypes(response.data);
      setSelectedTicketType(null);
      setAppliedCoupon(null);
      setPriceInfo(null);
    } catch (error) {
      console.error("Error fetching ticket types:", error);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    // Single source of truth for email-typo suggestions — also covers
    // programmatic writes (URL prefill, suggestion "Aplicar" button, future autofill).
    const field = customFields.find((f) => f.name === fieldName);
    if (field?.field_type === "email") {
      const s = suggestEmailFix(value);
      setEmailSuggestions((prev) => ({ ...prev, [fieldName]: s }));
    }
  };

  const handleTicketTypeSelect = (type) => {
    setSelectedTicketType(type);
    setPriceInfo({
      original_price: type.price,
      discount: 0,
      final_price: type.price
    });
    setAppliedCoupon(null);
    setCouponCode("");
    // Reset installment state — defaults depend on the new ticket type
    setPaymentMode("full");
    setPaymentModeTouched(false);
    setInstallmentSurchargeAccepted(false);
    setFirstInstallment("");
    setSelectedPlanShortcut(null);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !selectedTicketType) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API_URL}/api/validate-coupon`, {
        code: couponCode,
        ticket_type_id: selectedTicketType.id,
        email: (formData.email || "").trim().toLowerCase(),
      });
      
      setAppliedCoupon(response.data.coupon);
      setPriceInfo({
        original_price: response.data.original_price,
        discount: response.data.discount,
        final_price: response.data.final_price
      });
      toast.success("Cupão aplicado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cupão inválido");
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    if (selectedTicketType) {
      setPriceInfo({
        original_price: selectedTicketType.price,
        discount: 0,
        final_price: selectedTicketType.price
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSession) {
      toast.error("Por favor, selecione uma sessão");
      return;
    }
    if (!selectedTicketType) {
      toast.error("Por favor, selecione um tipo de bilhete");
      return;
    }

    // Validate required fields
    for (const field of customFields) {
      if (field.required && !formData[field.name]) {
        toast.error(`Por favor, preencha o campo: ${field.label}`);
        return;
      }
    }

    // Gate: require account when the admin turned on `require_account_for_purchase`.
    if (show?.require_account_for_purchase && !loggedInUser) {
      setShowAuthGate(true);
      return;
    }

    // Installment plan: extra validations
    const installmentsAllowed = selectedTicketType.installments_enabled && (priceInfo?.final_price ?? 0) > 0;
    let firstInstallmentValue = null;
    if (paymentMode === "installment") {
      if (!installmentsAllowed) {
        toast.error("Este bilhete não permite pagamento a prazo.");
        return;
      }
      if (!loggedInUser) {
        toast.error("Para pagar a prazo, entre ou crie uma conta primeiro.");
        return;
      }
      firstInstallmentValue = parseFloat(firstInstallment || "0");
      if (!firstInstallmentValue || firstInstallmentValue <= 0) {
        toast.error("Indique o valor da 1ª prestação.");
        return;
      }
      // Mirror backend: apply surcharge to total before validating installment limits
      const surchargePct = selectedTicketType.installment_surcharge_pct ?? show?.installment_surcharge_pct ?? 3;
      const total = (priceInfo?.final_price ?? 0) * (1 + surchargePct / 100);
      if (firstInstallmentValue > total + 0.01) {
        toast.error("A 1ª prestação não pode exceder o valor total.");
        return;
      }
      const minPct = parseFloat(selectedTicketType.min_first_payment_percent ?? 25);
      const minAmt = Math.round(total * minPct) / 100;
      if (firstInstallmentValue < minAmt - 0.005) {
        toast.error(`A 1ª prestação tem de ser pelo menos ${minAmt.toFixed(2)}€ (${minPct.toFixed(0)}% do total).`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Sprint 7 — parental gate (client-side mirror of backend rule)
      if (parentalRequired) {
        if (!parentalValid || !parentalPayload) {
          toast.error("Preencha e assine o Termo de Responsabilidade Parental.");
          setSubmitting(false);
          return;
        }
      }

      const response = await axios.post(
        `${API_URL}/api/register/v2`,
        {
          session_id: selectedSession.id,
          ticket_type_id: selectedTicketType.id,
          coupon_code: appliedCoupon?.code || null,
          custom_fields: formData,
          payment_mode: paymentMode,
          first_installment_amount: firstInstallmentValue,
          parental_auth: parentalRequired ? parentalPayload : null,
        },
        { withCredentials: true }
      );

      setRegistrationData(response.data);
      setRegistrationComplete(true);
      toast.success("Registo efetuado com sucesso!");
    } catch (error) {
      // Backend also gates anon buyers when `require_account_for_purchase`
      // is on. If we hit the 401 (frontend gate somehow slipped), open the
      // same Login/Register modal instead of showing a raw error toast.
      if (error.response?.status === 401 && error.response?.data?.detail === "account_required") {
        setShowAuthGate(true);
        return;
      }
      // Sprint 7 — surface structured parental errors with friendly messages
      const detail = error.response?.data?.detail;
      let message = "Erro ao efetuar registo";
      if (typeof detail === "string") message = detail;
      else if (detail && typeof detail === "object") message = detail.message || detail.code || message;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const setPaymentMethod = async (method) => {
    try {
      await axios.post(`${API_URL}/api/set-payment-method`, {
        registration_id: registrationData.registration_id,
        payment_method: method
      });
    } catch {}
  };

  const handlePaymentRedirect = async () => {
    if (registrationData?.stripe_enabled) {
      // Show the 60-min hold confirmation modal first
      setShowStripeConfirmModal(true);
    }
  };

  const proceedToStripe = async () => {
    setShowStripeConfirmModal(false);
    await setPaymentMethod("Stripe");
    try {
      const response = await axios.post(`${API_URL}/api/stripe/create-checkout`, {
        registration_id: registrationData.registration_id,
        origin_url: window.location.origin
      });
      if (response.data.free) {
        toast.success("Bilhete gratuito confirmado!");
        window.location.href = `/payment-return?registration_id=${registrationData.registration_id}`;
        return;
      }
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao iniciar pagamento Stripe");
    }
  };

  const handleExternalPayment = async () => {
    const title = registrationData?.external_payment_title || "Link Externo";
    await setPaymentMethod(title);
    localStorage.setItem("pending_registration_id", registrationData.registration_id);
    window.open(registrationData.payment_link, "_blank");
  };

  const handleOfflinePayment = async (location) => {
    await setPaymentMethod(location.title);
    setOfflineSelected(location);
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      required: field.required,
      className: "input-dark",
      "data-testid": `input-${field.name}`
    };

    switch (field.field_type) {
      case "text":
      case "phone":
        return (
          <Input
            {...commonProps}
            type="text"
            value={formData[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || field.label}
          />
        );
      case "email": {
        const suggestion = emailSuggestions[field.name];
        return (
          <div>
            <Input
              {...commonProps}
              type="email"
              value={formData[field.name] || ""}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || field.label}
              data-testid={`email-input-${field.name}`}
            />
            {suggestion && (
              <div
                className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
                data-testid={`email-typo-suggestion-${field.name}`}
              >
                <span>
                  Quis dizer <strong className="font-semibold text-amber-50">{suggestion.suggestion}</strong>?
                </span>
                <button
                  type="button"
                  onClick={() => handleFieldChange(field.name, suggestion.suggestion)}
                  className="rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-black hover:bg-amber-400 transition-colors"
                  data-testid={`email-typo-apply-${field.name}`}
                >
                  Aplicar
                </button>
              </div>
            )}
          </div>
        );
      }
      case "number":
        return (
          <Input
            {...commonProps}
            type="number"
            value={formData[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || field.label}
          />
        );
      case "date":
        return (
          <Input
            {...commonProps}
            type="date"
            value={formData[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
        );
      case "dropdown": {
        const isOther = !!otherSelected[field.name];
        // Defensively normalise options: strip whitespace, drop empty entries,
        // skip the internal `__OTHER__` marker if an admin accidentally typed
        // it in the options list, and dedupe.
        const safeOptions = Array.from(new Set(
          (field.options || [])
            .map((o) => (o == null ? "" : String(o).trim()))
            .filter((o) => o && o !== "__OTHER__")
        ));
        // Use a NATIVE <select> instead of Radix. Radix Select has multiple
        // known Android Chrome quirks (touch events firing on elements behind
        // the popover, poor keyboard/scroll interaction). Native <select>
        // gives us the OS-provided picker (perfect for touch) and works
        // reliably across every device.
        const currentValue = isOther ? "__OTHER__" : (formData[field.name] || "");
        return (
          <div>
            <select
              value={currentValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "__OTHER__") {
                  setOtherSelected((p) => ({ ...p, [field.name]: true }));
                  setFormData((p) => ({ ...p, [field.name]: "" }));
                  // Some Android devices leave the user staring at a black area
                  // below the form after selecting "Outro" because the newly
                  // appended text input is off-screen. Ensure the new input
                  // scrolls into view so the user always sees where to type.
                  setTimeout(() => {
                    document.querySelector(`[data-testid="input-other-${field.name}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
                    document.querySelector(`[data-testid="input-other-${field.name}"]`)?.focus?.();
                  }, 80);
                } else if (value === "") {
                  setOtherSelected((p) => ({ ...p, [field.name]: false }));
                  handleFieldChange(field.name, "");
                } else {
                  setOtherSelected((p) => ({ ...p, [field.name]: false }));
                  handleFieldChange(field.name, value);
                }
              }}
              required={field.required && !isOther}
              /* Android Chrome bug: when the site is dark and no color-scheme
                 is declared, the native fullscreen picker may render options as
                 dark-on-dark, giving the user a solid black screen. Forcing
                 color-scheme:light on the <select> element makes Chrome show
                 the picker in light mode (dark text on white) which is fully
                 legible everywhere. iOS/desktop are unaffected. */
              style={{ colorScheme: "light" }}
              className="w-full h-10 px-3 rounded-md border border-white/10 bg-zinc-900 text-white focus:border-amber-500/60 focus:outline-none"
              data-testid={`input-${field.name}`}
            >
              <option value="" disabled>
                {field.placeholder || `Selecione ${field.label.toLowerCase()}`}
              </option>
              {safeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              {field.allow_other && (
                <option value="__OTHER__" data-testid={`option-other-${field.name}`}>
                  Outro
                </option>
              )}
            </select>
            {isOther && (
              <Input
                type="text"
                value={formData[field.name] || ""}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder="Especifique a sua resposta"
                required={field.required}
                className="input-dark mt-2"
                data-testid={`input-other-${field.name}`}
              />
            )}
          </div>
        );
      }
      case "checkbox":
        return (
          <div className="flex items-center gap-3">
            <Checkbox
              id={field.name}
              checked={formData[field.name] || false}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
              className="border-zinc-500 data-[state=checked]:border-current"
              style={formData[field.name] ? { backgroundColor: ac, borderColor: ac } : {}}
              data-testid={`checkbox-${field.name}`}
            />
            <label htmlFor={field.name} className="text-zinc-300 text-sm cursor-pointer">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
          </div>
        );
      default:
        return (
          <Input
            {...commonProps}
            type="text"
            value={formData[field.name] || ""}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#f59e0b', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const ac = show?.accent_color || "#f59e0b";

  // ─── Inactive Landing (HeyLink-style) ────────────────────────────────
  // Rendered whenever the admin explicitly turned on the alternate landing
  // page, or when previewing it from the admin settings via ?inactive_preview=1.
  const inactivePreview = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("inactive_preview") === "1";
  const forceCheckout = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("checkout") === "1";
  if (show && (show.inactive_landing_enabled || inactivePreview) && !forceCheckout) {
    return <InactiveLandingView show={show} />;
  }

  return (
    <div
      className="min-h-screen bg-[#09090b] overflow-x-hidden"
      data-testid="landing-page"
      style={{
        "--ac": ac,
        "--ac-10": hexToRgba(ac, 0.1),
        "--ac-20": hexToRgba(ac, 0.2),
        "--ac-30": hexToRgba(ac, 0.3),
      }}
    >
      <AccountHeaderWidget accentColor={ac} />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-y-0 right-0 w-1/2 bg-contain bg-right bg-no-repeat hidden sm:block"
          style={{ backgroundImage: `url(${resolveImageUrl(show?.image_url)})` }}
        />
        <div className="absolute inset-y-0 right-0 w-1/2 hero-gradient hidden sm:block" />
        
        <div className="relative z-10 container mx-auto px-6 lg:px-12 py-24">
          {/* Mobile: square image on top, text left-aligned below */}
          <div className="sm:hidden mb-10">
            <img
              src={resolveImageUrl(show?.hero_image_mobile_url || show?.image_url)}
              alt={show?.name}
              className="w-full aspect-square object-cover rounded-2xl shadow-2xl"
            />
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-left mx-0"
          >
            {show?.show_hero_badge !== false && (
              <span className="text-sm uppercase tracking-[0.2em] font-medium mb-6 block" style={{ color: ac }}>
                {show?.hero_badge || "Evento Especial"}
              </span>
            )}
            {show?.show_hero_title !== false && (
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-none text-white mb-8">
                {show?.name}
              </h1>
            )}
            {show?.show_hero_description !== false && (
              <p className="text-base sm:text-lg text-white leading-relaxed mb-10 max-w-xl">
                {show?.description}
              </p>
            )}
            
            <div className="space-y-4 mb-10 items-start">
              {(show?.show_hero_location !== false || show?.show_hero_address !== false) && (
                <div className="flex items-start gap-3 text-white justify-start">
                  <MapPin className="w-5 h-5 shrink-0 mt-1" style={{ color: ac }} />
                  <div className="flex flex-col">
                    {show?.show_hero_location !== false && show?.location && <span>{show.location}</span>}
                    {show?.show_hero_address !== false && show?.address && (
                      <span className="text-zinc-400 text-sm">{show.address}</span>
                    )}
                  </div>
                </div>
              )}
              {(show?.show_hero_sessions_text !== false || show?.show_hero_sessions_subtitle !== false) && (
                <div className="flex items-center gap-3 text-white justify-start">
                  <Calendar className="w-5 h-5 shrink-0" style={{ color: ac }} />
                  <span>
                    {show?.show_hero_sessions_text !== false && (show?.hero_sessions_text || `${sessions.length} Sessões Disponíveis`)}
                    {show?.show_hero_sessions_text !== false && show?.show_hero_sessions_subtitle !== false && <span className="text-white"> • </span>}
                    {show?.show_hero_sessions_subtitle !== false && (
                      <span className="text-white">{show?.hero_sessions_subtitle || "Pode escolher o melhor horário"}</span>
                    )}
                  </span>
                </div>
              )}
              {show?.show_hero_price !== false && (
                <div className="flex items-center gap-3 text-white justify-start">
                  <Ticket className="w-5 h-5 shrink-0" style={{ color: ac }} />
                  <span>
                    {show?.show_hero_price_text !== false && (show?.hero_price_text || "A partir de")}{" "}
                    <span className="text-xl font-bold text-white">{show?.price?.toFixed(2)}€</span>
                  </span>
                </div>
              )}
            </div>
            
            {show?.show_hero_cta !== false && (
              <Button
                onClick={() => document.getElementById("tickets")?.scrollIntoView({ behavior: "smooth" })}
                className="px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg rounded-full font-semibold transition-all hover:opacity-90 w-full sm:w-auto"
                style={{ backgroundColor: ac, color: "#000", boxShadow: `0 4px 14px 0 ${hexToRgba(ac, 0.3)}` }}
                data-testid="buy-ticket-btn"
              >
                {show?.cta_button_text || "Comprar Bilhete"}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            )}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-[#0f0f12]" data-testid="about-section">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {show?.show_about_badge !== false && (
                <span className="text-sm uppercase tracking-[0.2em] font-medium mb-4 block" style={{ color: ac }}>
                  {show?.about_badge || "Sobre o Espectáculo"}
                </span>
              )}
              {show?.show_about_title !== false && (
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">
                  {show?.about_title || "Uma Experiência Inesquecível"}
                </h2>
              )}
              
              {/* Expandable rich content */}
              {show?.show_about_description !== false && show?.about_description_html && (
                <>
                  {aboutExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.3 }}
                      className="about-rich-content mb-4"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(show.about_description_html) }}
                      data-testid="about-rich-content"
                    />
                  )}
                  <button
                    onClick={() => setAboutExpanded(!aboutExpanded)}
                    className="text-sm font-medium transition-colors mb-6 flex items-center gap-1.5 hover:opacity-80"
                    style={{ color: ac }}
                    data-testid="about-see-more-btn"
                  >
                    {aboutExpanded ? "ver menos" : "ver mais info"}
                    <ChevronRight
                      className="w-4 h-4 transition-transform"
                      style={{ transform: aboutExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                    />
                  </button>
                </>
              )}

              {show?.show_about_info_button !== false && show?.about_info_button_text && show?.about_info_button_url && (
                <div className="mb-6">
                  <a
                    href={show.about_info_button_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 rounded-full font-semibold transition-all hover:opacity-90"
                    style={{ backgroundColor: ac, color: "#000" }}
                    data-testid="about-info-button"
                  >
                    {show.about_info_button_text}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </a>
                </div>
              )}

              <div className="space-y-4">
                {show?.show_about_terms !== false && show?.terms_url && (
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(ac, 0.1) }}>
                      <FileText className="w-5 h-5" style={{ color: ac }} />
                    </div>
                    <a href={show.terms_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-testid="terms-link">
                      <p className="font-medium" style={{ color: ac }}>{show?.terms_label || "Termos e Condições"}</p>
                      <p className="text-white text-xs">clique aqui para ler</p>
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {show?.about_media_type === "video" && show?.about_video_url ? (
                <iframe
                  src={show.about_video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").split("&")[0]}
                  title="Video"
                  className="rounded-2xl shadow-2xl w-full aspect-video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <img
                  src={resolveImageUrl(show?.about_image_url) || "https://images.unsplash.com/photo-1585704169985-1e01f04964d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHx0aGVhdHJlJTIwc3RhZ2UlMjBzaG93JTIwYXVkaWVuY2V8ZW58MHx8fHwxNzc1NDcxOTQxfDA&ixlib=rb-4.1.0&q=85"}
                  alt="Performance"
                  className="rounded-2xl shadow-2xl w-full"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tickets Section */}
      <section id="tickets" className="py-24 bg-[#09090b]" data-testid="tickets-section">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm uppercase tracking-[0.2em] font-medium mb-4 block" style={{ color: ac }}>
              {show?.tickets_badge || "Reserve Já"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {show?.tickets_title || "Escolha a Sua Sessão"}
            </h2>
            {show?.tickets_info_html && (
              <div
                className="prose prose-invert prose-sm max-w-2xl mx-auto mt-5 text-zinc-300/90 [&_a]:text-amber-400 [&_strong]:text-white"
                data-testid="tickets-info-html"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(show.tickets_info_html) }}
              />
            )}
          </motion.div>

          {!registrationComplete ? (
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Sessions & Ticket Types */}
              <div className="space-y-6">
                {show?.tickets_step1_show_public !== false && (
                  <h3 className="text-lg font-semibold text-white mb-4">{show?.tickets_step1_title || "1. Escolha a Sessão"}</h3>
                )}
                {rooms.length > 0 && (
                  <div className="space-y-3 mb-6" data-testid="room-cards-wrap">
                    {show?.rooms_step_show_public !== false && (
                      <p className="text-zinc-400 text-xs uppercase tracking-wider">{show?.rooms_step_title || "Escolha a Sala"}</p>
                    )}
                    {[
                      ...rooms.map((r) => ({ id: r.id, name: r.name, description: r.description, isNone: false })),
                      ...(sessions.some((s) => !s.room_id) ? [{ id: "__NONE__", name: "Outras sessões", description: "Sessões sem sala específica", isNone: true }] : []),
                    ].map((r) => {
                      const isSelected = selectedRoomId === r.id;
                      const count = sessions.filter((s) => (r.isNone ? !s.room_id : s.room_id === r.id)).length;
                      if (count === 0) return null;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setSelectedRoomId(isSelected ? "__ALL__" : r.id)}
                          className={`w-full p-4 rounded-lg border transition-all text-left ${
                            isSelected
                              ? "border-amber-500 bg-amber-500/10"
                              : "border-white/10 bg-white/5 hover:bg-white/10"
                          }`}
                          data-testid={`room-card-${r.id}`}
                          style={isSelected ? { borderColor: ac, backgroundColor: hexToRgba(ac, 0.1) } : {}}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(ac, 0.1) }}>
                                {(() => {
                                  if (r.isNone) return <Calendar className="w-6 h-6" style={{ color: ac }} />;
                                  const IconComp = ROOM_ICONS[(rooms.find((x) => x.id === r.id) || {}).icon] || Building2;
                                  return <IconComp className="w-6 h-6" style={{ color: ac }} />;
                                })()}
                              </div>
                              <div>
                                <p className="text-white font-semibold">{r.name}</p>
                                {r.description && (
                                  <p className="text-zinc-400 text-sm mt-0.5">{r.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {(() => {
                                const cfg = r.isNone ? null : rooms.find((x) => x.id === r.id);
                                if (cfg && cfg.show_session_count === false) return null;
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: hexToRgba(ac, 0.15), color: ac }}>
                                    {count} {count === 1 ? "sessão" : "sessões"}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {(rooms.length === 0 || selectedRoomId !== "__ALL__") && (
                  show?.sessions_step_show_public !== false && rooms.length > 0 ? (
                    <p className="text-zinc-400 text-xs uppercase tracking-wider mb-3">
                      {show?.sessions_step_title || "Escolha a Sessão"}
                    </p>
                  ) : null
                )}
                {sessions
                  .filter((s) => {
                    if (rooms.length === 0) return true;
                    if (selectedRoomId === "__ALL__") return false; // require room pick first
                    if (selectedRoomId === "__NONE__") return !s.room_id;
                    return s.room_id === selectedRoomId;
                  })
                  .map((session, index) => {
                  const isSoldOut = (session.available_seats || 0) <= 0;
                  return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    onClick={() => {
                      if (isSoldOut) return;
                      setSelectedSession(session);
                    }}
                    className={`session-card ${selectedSession?.id === session.id ? "" : ""} ${isSoldOut ? "opacity-60 cursor-not-allowed" : ""}`}
                    style={selectedSession?.id === session.id ? { borderColor: ac, backgroundColor: hexToRgba(ac, 0.05) } : {}}
                    data-testid={`session-card-${index}`}
                    aria-disabled={isSoldOut}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(ac, 0.1) }}>
                          <Calendar className="w-6 h-6" style={{ color: ac }} />
                        </div>
                        <div>
                          {session.name && session.show_name !== false && (
                            <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider mb-0.5">{session.name}</p>
                          )}
                          {session.subtitle && session.show_subtitle !== false && (
                            <p className="text-zinc-300 text-sm font-medium uppercase tracking-wider mb-0.5">{session.subtitle}</p>
                          )}
                          {session.show_date !== false && (
                            <p className="text-white font-semibold">{formatSessionDate(session.date)}</p>
                          )}
                          {session.show_time !== false && (
                            <div className="flex items-center gap-2 text-zinc-300 text-base mt-0.5">
                              <Clock className="w-4 h-4" />
                              <span className="font-medium">{session.time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {isSoldOut ? (
                          <>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 text-xs font-semibold">
                              <Users className="w-3.5 h-3.5" />
                              Esgotado de momento
                            </span>
                            <p className="text-zinc-500 text-xs mt-1 max-w-[180px] leading-tight">
                              Reservas pendentes podem libertar. Tente mais tarde.
                            </p>
                          </>
                        ) : (
                          session.show_total_seats !== false && (
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                              <Users className="w-4 h-4" />
                              <span>{session.available_seats} lugares</span>
                            </div>
                          )
                        )}
                        {!isSoldOut && selectedSession?.id === session.id && (
                          <div className="flex items-center gap-1 text-sm mt-1" style={{ color: ac }}>
                            <Check className="w-4 h-4" />
                            <span>Selecionado</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  );
                })}

                {/* Ticket Types */}
                {selectedSession && ticketTypes.length > 0 && (
                  <div className="mt-8">
                    {show?.tickets_step2_show_public !== false && (
                      <h3 className="text-lg font-semibold text-white mb-4">{show?.tickets_step2_title || "2. Escolha o Tipo de Bilhete"}</h3>
                    )}
                    <div className="space-y-3">
                      {ticketTypes.map((type) => (
                        <div
                          key={type.id}
                          onClick={() => handleTicketTypeSelect(type)}
                          className={`p-4 rounded-xl border cursor-pointer transition-all ${
                            selectedTicketType?.id === type.id
                              ? ""
                              : "border-white/10 bg-zinc-900 hover:border-white/20"
                          } ${type.limit !== -1 && type.sold >= type.limit ? "opacity-50 cursor-not-allowed" : ""}`}
                          style={selectedTicketType?.id === type.id ? { borderColor: ac, backgroundColor: hexToRgba(ac, 0.1) } : {}}
                          data-testid={`ticket-type-${type.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Ticket className="w-5 h-5" style={{ color: ac }} />
                              <div>
                                {type.show_name !== false && (
                                  <p className="text-white font-medium">{type.name}</p>
                                )}
                                {type.show_availability !== false && type.limit !== -1 && (
                                  <p className="text-zinc-500 text-sm">
                                    {type.limit - type.sold} disponíveis
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {type.show_price !== false && (
                                <p className="text-xl font-bold" style={{ color: ac }}>{type.price.toFixed(2)}€</p>
                              )}
                              {type.limit !== -1 && type.sold >= type.limit && (
                                <p className="text-red-400 text-sm">Esgotado</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Coupon */}
                {selectedTicketType && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Cupão de Desconto (opcional)</h3>
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <Input
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Código do cupão"
                          className="input-dark font-mono"
                          data-testid="coupon-input"
                        />
                        <Button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode.trim()}
                          className="btn-secondary"
                          data-testid="apply-coupon-btn"
                        >
                          {validatingCoupon ? "..." : "Aplicar"}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Tag className="w-5 h-5 text-green-500" />
                          <span className="text-green-500 font-mono font-bold">{appliedCoupon.code}</span>
                          <span className="text-zinc-400">
                            ({appliedCoupon.discount_type === "percentage" 
                              ? `${appliedCoupon.discount_value}%` 
                              : `${appliedCoupon.discount_value}€`} desconto)
                          </span>
                        </div>
                        <button onClick={removeCoupon} className="text-zinc-500 hover:text-red-400">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    )}

                    {/* Price Summary */}
                    {priceInfo && (
                      <div className="mt-4 p-4 bg-zinc-900 rounded-lg border border-white/10">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Preço original</span>
                            <span className="text-white">{priceInfo.original_price.toFixed(2)}€</span>
                          </div>
                          {priceInfo.discount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-green-500">Desconto</span>
                              <span className="text-green-500">-{priceInfo.discount.toFixed(2)}€</span>
                            </div>
                          )}
                          <div className="flex justify-between text-lg font-bold border-t border-white/10 pt-2 mt-2">
                            <span className="text-white">Total</span>
                            <span style={{ color: ac }}>{priceInfo.final_price.toFixed(2)}€</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ─── Payment Mode (Installments) ───────────────── */}
                    {selectedTicketType?.installments_enabled && (priceInfo?.final_price ?? 0) > 0 && (() => {
                      const surchargePct = selectedTicketType?.installment_surcharge_pct ?? show?.installment_surcharge_pct ?? 3;
                      const surchargeAmount = paymentMode === "installment" ? (priceInfo.final_price * surchargePct / 100) : 0;
                      const totalWithSurcharge = priceInfo.final_price + surchargeAmount;
                      const minFirst = totalWithSurcharge * (selectedTicketType.min_first_payment_percent ?? 25) / 100;
                      return (
                      <div className="mt-5 p-4 bg-zinc-900/60 rounded-xl border border-amber-500/25" data-testid="payment-mode-block">
                        <h4 className="text-white font-semibold mb-1">Como prefere pagar?</h4>
                        <p className="text-zinc-400 text-xs mb-3">Pode pagar tudo agora ou em prestações.</p>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <button
                            type="button"
                            onClick={() => { setPaymentMode("full"); setPaymentModeTouched(true); setFirstInstallment(""); setSelectedPlanShortcut(null); setInstallmentSurchargeAccepted(false); }}
                            className={`p-3 rounded-lg border text-left transition-colors ${paymentMode === "full" ? "border-amber-400 bg-amber-500/15" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
                            data-testid="pay-mode-full"
                          >
                            <p className="text-sm font-medium text-white">Pagar Totalidade</p>
                            <p className="text-xs text-zinc-400">Pague {priceInfo.final_price.toFixed(2)}€ agora</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPaymentMode("installment"); setPaymentModeTouched(true); setInstallmentSurchargeAccepted(false); }}
                            className={`p-3 rounded-lg border text-left transition-colors ${paymentMode === "installment" ? "border-amber-400 bg-amber-500/15" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
                            data-testid="pay-mode-installment"
                          >
                            <p className="text-sm font-medium text-white">Em prestações</p>
                            <p className="text-xs text-zinc-400">
                              {surchargePct > 0 ? `Pague em várias vezes (+${surchargePct}% taxa)` : "Pague em várias vezes"}
                            </p>
                          </button>
                        </div>

                        {paymentMode === "installment" && (
                          <div className="space-y-3 pt-2 border-t border-white/5">
                            {/* Surcharge notice + acceptance gate */}
                            {surchargePct > 0 && (
                              <div className="rounded-lg p-3 border border-amber-500/40 bg-amber-500/10" data-testid="installment-surcharge-notice">
                                <p className="text-amber-200 text-sm font-medium mb-2">⚠️ Acréscimo por pagamento em prestações</p>
                                <div className="text-xs text-amber-100/90 space-y-1 mb-3">
                                  <p>Ao optar por pagar em prestações, é aplicada uma taxa de <strong>{surchargePct}%</strong> sobre o valor final para cobrir custos de processamento.</p>
                                  <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-amber-500/20">
                                    <div>
                                      <p className="text-amber-100/60">Valor original</p>
                                      <p className="text-amber-100 font-mono">{priceInfo.final_price.toFixed(2)}€</p>
                                    </div>
                                    <div>
                                      <p className="text-amber-100/60">+ Taxa ({surchargePct}%)</p>
                                      <p className="text-amber-100 font-mono">+{surchargeAmount.toFixed(2)}€</p>
                                    </div>
                                    <div className="col-span-2 pt-1 mt-1 border-t border-amber-500/20">
                                      <p className="text-amber-100/60">Novo total a pagar em prestações</p>
                                      <p className="text-amber-200 font-bold font-mono text-base">{totalWithSurcharge.toFixed(2)}€</p>
                                    </div>
                                  </div>
                                </div>
                                <label className="flex items-start gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={installmentSurchargeAccepted}
                                    onChange={(e) => setInstallmentSurchargeAccepted(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 rounded border-amber-500/40 bg-amber-500/10 accent-amber-400"
                                    data-testid="installment-surcharge-accept"
                                  />
                                  <span className="text-xs text-amber-100">
                                    Compreendi e aceito a taxa de <strong>{surchargePct}%</strong> ({surchargeAmount.toFixed(2)}€). O total a pagar passa a ser <strong>{totalWithSurcharge.toFixed(2)}€</strong>.
                                  </span>
                                </label>
                              </div>
                            )}

                            {(surchargePct === 0 || installmentSurchargeAccepted) && (!loggedInUser ? (
                              <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="installment-login-warning">
                                <p className="font-medium">Conta necessária</p>
                                <p className="text-amber-200/80 mt-1">Para pagar a prazo, <Link to="/login" className="underline">entre</Link> ou <Link to="/registar" className="underline">crie uma conta</Link>. Os dados que preencheu não se perdem.</p>
                              </div>
                            ) : (
                              <>
                                {(selectedTicketType.suggested_plans || [2, 3, 4]).length > 0 && (
                                  <div>
                                    <p className="text-zinc-300 text-xs mb-1.5">Sugestões rápidas (1ª prestação)</p>
                                    <div className="flex gap-2 flex-wrap">
                                      {(selectedTicketType.suggested_plans || [2, 3, 4]).map((n) => {
                                        const v = (totalWithSurcharge / n);
                                        const isSel = selectedPlanShortcut === n;
                                        return (
                                          <button
                                            key={n}
                                            type="button"
                                            onClick={() => { setSelectedPlanShortcut(n); setFirstInstallment(v.toFixed(2)); }}
                                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isSel ? "bg-amber-500/25 border-amber-500/50 text-amber-200" : "bg-white/5 border-white/10 text-zinc-300 hover:text-white"}`}
                                            data-testid={`installment-shortcut-${n}x`}
                                          >
                                            {n}x · {v.toFixed(2)}€
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <Label className="text-zinc-300 text-xs">Valor da 1ª prestação (€)</Label>
                                  <Input
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min={minFirst.toFixed(2)}
                                    max={totalWithSurcharge}
                                    value={firstInstallment}
                                    onChange={(e) => { setFirstInstallment(e.target.value); setSelectedPlanShortcut(null); }}
                                    placeholder={`Mínimo ${minFirst.toFixed(2)}€`}
                                    className="input-dark mt-1"
                                    data-testid="installment-first-amount"
                                  />
                                  <p className="text-[11px] text-zinc-500 mt-1">
                                    Mínimo: <span className="text-zinc-300">{minFirst.toFixed(2)}€</span> ({selectedTicketType.min_first_payment_percent ?? 25}% do total) · Restante depois: <span className="text-zinc-300">{Math.max(0, totalWithSurcharge - (parseFloat(firstInstallment) || 0)).toFixed(2)}€</span>
                                  </p>
                                </div>
                              </>
                            ))}
                          </div>
                        )}
                      </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Registration Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="glass rounded-2xl p-8"
              >
                {show?.tickets_step3_show_public !== false && (
                  <h3 className="text-lg font-semibold text-white mb-6">{show?.tickets_step3_title || "3. Dados de Registo"}</h3>
                )}
                {(() => {
                  // Account gate takes precedence: if the admin toggled
                  // `require_account_for_purchase`, an anonymous visitor sees
                  // the Login/Register call-to-action here — but can still
                  // freely browse rooms/sessions/tickets and see prices above.
                  if (show?.require_account_for_purchase && !loggedInUser) {
                    return (
                      <div
                        className="rounded-xl p-6 border border-amber-500/30 bg-amber-500/[0.06] text-center"
                        data-testid="require-account-block"
                      >
                        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: hexToRgba(ac, 0.15) }}>
                          <Ticket className="w-7 h-7" style={{ color: ac }} />
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">Conta necessária para comprar</h4>
                        <p className="text-zinc-400 text-sm mb-5 max-w-sm mx-auto">
                          Para garantir que os seus bilhetes ficam sempre acessíveis, entre na sua conta ou crie uma nova antes de preencher os dados.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
                          <button
                            type="button"
                            onClick={() => navigate("/login", { state: { from: "/#tickets" } })}
                            className="rounded-xl border border-white/10 bg-white/[0.03] p-3 hover:border-amber-500/50 hover:bg-white/[0.06] transition-colors"
                            data-testid="require-account-login"
                          >
                            <p className="text-white font-medium text-sm">Já tenho conta</p>
                            <p className="text-zinc-500 text-xs mt-0.5">Entrar</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate("/registar", { state: { from: "/#tickets" } })}
                            className="rounded-xl p-3 hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: ac, color: "#000" }}
                            data-testid="require-account-register"
                          >
                            <p className="font-semibold text-sm">Criar conta nova</p>
                            <p className="text-xs opacity-80 mt-0.5">Demora 30 segundos</p>
                          </button>
                        </div>
                      </div>
                    );
                  }
                  // Gate: user must complete step 1 (session), step 2 (ticket type), and
                  // — if installments are enabled — explicitly pick a payment mode before
                  // the registration form becomes editable. If they pick installments
                  // AND there's a surcharge, they must also acknowledge it via checkbox.
                  const installmentsAvail = !!selectedTicketType?.installments_enabled && (priceInfo?.final_price ?? 0) > 0;
                  const needPaymentChoice = installmentsAvail && !paymentModeTouched;
                  const surchargePctEff = selectedTicketType?.installment_surcharge_pct ?? show?.installment_surcharge_pct ?? 3;
                  const needSurchargeAccept = installmentsAvail && paymentMode === "installment" && surchargePctEff > 0 && !installmentSurchargeAccepted;
                  const missingSteps = [];
                  if (!selectedSession) missingSteps.push("Escolha a sessão (passo 1)");
                  else if (!selectedTicketType) missingSteps.push("Escolha o tipo de bilhete (passo 2)");
                  else if (needPaymentChoice) missingSteps.push("Escolha como prefere pagar");
                  else if (needSurchargeAccept) missingSteps.push(`Aceite a taxa de ${surchargePctEff}% das prestações`);
                  const formLocked = missingSteps.length > 0;
                  if (formLocked) {
                    return (
                      <div
                        className="border border-dashed border-white/15 rounded-xl p-6 bg-white/[0.02] text-center"
                        data-testid="registration-form-locked"
                      >
                        <Tag className="w-7 h-7 mx-auto mb-3 text-zinc-500" />
                        <p className="text-zinc-300 text-sm font-medium mb-2">Complete os passos anteriores para registar os seus dados</p>
                        <ul className="text-zinc-500 text-xs space-y-1">
                          {missingSteps.map((s, i) => (
                            <li key={i}>• {s}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  return (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="registration-form">
                  {customFields.map((field) => (
                    <div key={field.id}>
                      {field.field_type !== "checkbox" && (
                        <Label htmlFor={field.name} className="text-zinc-300 mb-2 block">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </Label>
                      )}
                      {renderField(field)}
                    </div>
                  ))}

                  {parentalRequired && (
                    <ParentalAuthSection
                      ticketType={selectedTicketType}
                      sessionId={selectedSession?.id}
                      show={show}
                      onChange={handleParentalChange}
                    />
                  )}

                  <Button
                    type="submit"
                    disabled={!selectedSession || !selectedTicketType || submitting || (parentalRequired && !parentalValid)}
                    className="w-full py-6 text-lg rounded-xl mt-4 font-semibold transition-all hover:opacity-90"
                    style={{ backgroundColor: ac, color: "#000" }}
                    data-testid="submit-registration-btn"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        A processar...
                      </span>
                    ) : (() => {
                      // Mirror backend surcharge logic for the button label
                      const pct = paymentMode === "installment" ? (selectedTicketType?.installment_surcharge_pct ?? show?.installment_surcharge_pct ?? 3) : 0;
                      const finalShown = priceInfo ? priceInfo.final_price * (1 + pct / 100) : 0;
                      return (
                        <>
                          Continuar para Pagamento
                          {priceInfo && (
                            <span className="ml-2">({finalShown.toFixed(2)}€)</span>
                          )}
                          <ChevronRight className="ml-2 w-5 h-5" />
                        </>
                      );
                    })()}
                  </Button>
                </form>
                  );
                })()}
              </motion.div>
            </div>
          ) : (
            /* Registration Complete */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-xl mx-auto glass rounded-2xl p-8 text-center"
              data-testid="registration-success"
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: hexToRgba(ac, 0.2) }}>
                <Check className="w-8 h-8" style={{ color: ac }} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Registo Efetuado!</h3>
              <p className="text-zinc-400 mb-6">
                O seu registo foi efetuado com sucesso. Por favor, complete o pagamento para receber o seu bilhete.
              </p>
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-4">
                <p className="text-zinc-400 text-sm">Número do Bilhete</p>
                <p className="text-xl font-bold font-mono" style={{ color: ac }}>{registrationData?.ticket_number}</p>
              </div>
              {registrationData?.discount_amount > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                  <p className="text-green-500 text-sm">Desconto aplicado: -{registrationData.discount_amount.toFixed(2)}€</p>
                </div>
              )}
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <p className="text-zinc-400 text-sm">Valor a Pagar</p>
                <p className="text-2xl font-bold text-white">{registrationData?.final_price?.toFixed(2)}€</p>
              </div>

              {/* Step 1: Choose Online or Offline */}
              {!showOfflineOptions && !offlineSelected && (
                <div className="space-y-3">
                  {registrationData?.stripe_enabled && (
                    <button
                      onClick={handlePaymentRedirect}
                      className="w-full p-4 rounded-xl border border-white/10 bg-zinc-800/50 hover:bg-zinc-800 transition-all text-center"
                      data-testid="payment-online-btn"
                    >
                      <p className="font-semibold text-lg" style={{ color: ac }}>Pagar Agora (Online)</p>
                      <p className="text-zinc-400 text-xs mt-1">Cartões, Wallets, MBWay, MB, Débito SEPA</p>
                    </button>
                  )}
                  {registrationData?.external_payment_enabled && registrationData?.payment_link && (
                    <button
                      onClick={handleExternalPayment}
                      className="w-full p-4 rounded-xl border border-white/10 bg-zinc-800/50 hover:bg-zinc-800 transition-all text-center"
                      data-testid="payment-external-btn"
                    >
                      <p className="font-semibold text-lg" style={{ color: ac }}>{registrationData?.external_payment_title || "Pagar Online"}</p>
                      {registrationData?.external_payment_description && (
                        <p className="text-zinc-400 text-xs mt-1">{registrationData.external_payment_description}</p>
                      )}
                    </button>
                  )}
                  {registrationData?.offline_enabled && registrationData?.offline_locations?.filter(l => l.active !== false).length > 0 && (
                    <button
                      onClick={() => setShowOfflineOptions(true)}
                      className="w-full p-4 rounded-xl border border-white/10 bg-zinc-800/50 hover:bg-zinc-800 transition-all text-center"
                      data-testid="payment-offline-btn"
                    >
                      <p className="font-semibold text-lg" style={{ color: ac }}>Pagar Depois (Offline)</p>
                      <p className="text-zinc-400 text-xs mt-1">Presencialmente num local de pagamento</p>
                    </button>
                  )}
                </div>
              )}

              {/* Step 2: Choose Offline Location */}
              {showOfflineOptions && !offlineSelected && (
                <div className="space-y-3">
                  <p className="text-zinc-400 text-sm mb-2">Escolha onde pretende pagar:</p>
                  {registrationData?.offline_locations?.filter(l => l.active !== false).map((loc, i) => (
                    <button
                      key={i}
                      onClick={() => handleOfflinePayment(loc)}
                      className="w-full p-4 rounded-xl border border-white/10 bg-zinc-800/50 hover:bg-zinc-800 transition-all text-center"
                      data-testid={`offline-location-${i}`}
                    >
                      <p className="font-semibold" style={{ color: ac }}>{loc.title}</p>
                      {loc.methods && <p className="text-zinc-400 text-xs mt-1">{loc.methods}</p>}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowOfflineOptions(false)}
                    className="text-zinc-500 text-sm hover:text-zinc-300 mt-2 underline w-full text-center"
                  >
                    Voltar
                  </button>
                </div>
              )}

              {/* Step 3: Offline Confirmation */}
              {offlineSelected && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl border-2" style={{ borderColor: ac, backgroundColor: `${ac}10` }}>
                    <p className="font-semibold text-white mb-1">{offlineSelected.title}</p>
                    {offlineSelected.methods && (
                      <p className="text-zinc-400 text-sm">Formas de pagamento: {offlineSelected.methods}</p>
                    )}
                  </div>
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-400 text-sm font-medium">Reserva efetuada com sucesso!</p>
                    <p className="text-zinc-400 text-xs mt-2">
                      O seu bilhete só será enviado por email após a confirmação do pagamento na totalidade.
                      Guarde o seu número de bilhete: <span className="font-mono font-bold" style={{ color: ac }}>{registrationData?.ticket_number}</span>
                    </p>
                  </div>
                </div>
              )}

              {registrationData?.external_payment_enabled && !showOfflineOptions && !offlineSelected && (
                <>
                  <p className="text-zinc-500 text-sm mt-4">
                    Após o pagamento, volte a esta página e clique em "Confirmar Pagamento"
                  </p>
                  <Button
                    onClick={() => window.location.href = `/payment-return?registration_id=${registrationData?.registration_id}&external=true`}
                    variant="outline"
                    className="mt-4 btn-secondary"
                    data-testid="confirm-payment-btn"
                  >
                    Já efetuei o pagamento
                  </Button>
                </>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0f0f12] border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              {show?.show_footer_org_name !== false && (
                <h3 className="text-xl font-bold text-white" data-testid="footer-org-name">
                  {show?.footer_org_name || show?.name}
                </h3>
              )}
              {show?.show_footer_venue_name !== false && (
                <p className="text-zinc-500 text-sm" data-testid="footer-venue-name">
                  {show?.footer_venue_name || show?.location}
                </p>
              )}
            </div>
            <div className="flex items-center gap-6">
              {/* Social Links - Individual toggles */}
              {show?.social_instagram && show?.show_social_instagram !== false && (
                <a href={show.social_instagram} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              )}
              {show?.social_facebook && show?.show_social_facebook !== false && (
                <a href={show.social_facebook} target="_blank" rel="noopener noreferrer" 
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {show?.social_youtube && show?.show_social_youtube !== false && (
                <a href={show.social_youtube} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-youtube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              )}
              {show?.social_twitter && show?.show_social_twitter !== false && (
                <a href={show.social_twitter} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              )}
              {show?.social_tiktok && show?.show_social_tiktok !== false && (
                <a href={show.social_tiktok} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-tiktok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
              )}
              {show?.social_whatsapp && show?.show_social_whatsapp !== false && (
                <a href={show.social_whatsapp} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-whatsapp">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
              )}
              {show?.social_email && show?.show_social_email !== false && (
                <a href={show.social_email.startsWith("mailto:") ? show.social_email : `mailto:${show.social_email}`} 
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-email">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </a>
              )}
              {show?.social_website && show?.show_social_website !== false && (
                <a href={show.social_website} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-website">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
                  </svg>
                </a>
              )}
              {show?.social_custom && show?.show_social_custom !== false && (
                <a href={show.social_custom} target="_blank" rel="noopener noreferrer"
                   className="text-zinc-500 transition-colors hover:opacity-80" data-testid="social-custom">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                  </svg>
                </a>
              )}
              <a href="/admin/login" className="text-zinc-500 transition-colors hover:opacity-80 text-sm">
                Área Admin
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-zinc-500 text-sm">
            © {new Date().getFullYear()} {show?.show_footer_text !== false && (show?.footer_text || "Todos os direitos reservados")}
            {show?.show_footer_copyright !== false && show?.footer_copyright && <span> • {show.footer_copyright}</span>}
          </div>
        </div>
      </footer>

      {/* Stripe 60-min Hold Confirmation Modal */}
      {showStripeConfirmModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          data-testid="stripe-hold-modal"
        >
          <div
            className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl"
            style={{ boxShadow: `0 0 0 1px ${ac}33, 0 30px 80px rgba(0,0,0,0.6)` }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `${ac}1a`, border: `1px solid ${ac}55` }}
            >
              <span className="text-3xl">⏱️</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-3">
              60 minutos para pagar
            </h2>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-5">
              <p className="text-amber-100 text-sm md:text-base leading-relaxed">
                Ao avançar, o seu lugar fica <strong>reservado durante 60 minutos</strong>.
              </p>
              <p className="text-amber-100 text-sm md:text-base leading-relaxed mt-2">
                Se <strong>não concluir o pagamento dentro deste tempo</strong>, a reserva expira automaticamente e o bilhete fica caducado — o lugar volta a ficar disponível para outros compradores.
              </p>
            </div>
            <p className="text-zinc-400 text-xs text-center mb-6">
              Receberá um aviso por email aos 30 minutos. Conclua o pagamento antes dos 60 minutos para garantir o seu bilhete.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowStripeConfirmModal(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-zinc-300 hover:bg-white/5 transition-colors text-sm font-medium"
                data-testid="stripe-hold-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={proceedToStripe}
                className="flex-1 px-4 py-3 rounded-xl text-black font-semibold text-sm transition-transform hover:scale-[1.02]"
                style={{ background: ac }}
                data-testid="stripe-hold-proceed-btn"
              >
                Compreendi · Avançar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Gate — buyers must sign in / register before completing the purchase */}
      {showAuthGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowAuthGate(false)}
          data-testid="auth-gate-modal"
        >
          <div
            className="bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold text-white mb-2">Precisa de conta para comprar</h3>
            <p className="text-zinc-400 text-sm mb-5">
              Para garantir que os seus bilhetes ficam sempre acessíveis, tem de entrar na sua conta (ou criar uma nova). Os dados que preencheu não se perdem — voltará automaticamente para completar a compra.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => navigate("/login", { state: { from: "/#tickets", prefillEmail: formData.email } })}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:border-amber-500/40 hover:bg-white/[0.06] transition-colors"
                data-testid="auth-gate-login"
              >
                <p className="text-white font-medium">Já tenho conta</p>
                <p className="text-zinc-500 text-xs mt-1">Entrar com email e password</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/registar", { state: { from: "/#tickets", prefillEmail: formData.email } })}
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-left hover:border-amber-400 hover:bg-amber-500/15 transition-colors"
                data-testid="auth-gate-register"
              >
                <p className="text-white font-medium">Criar conta nova</p>
                <p className="text-zinc-500 text-xs mt-1">Demora 30 segundos</p>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowAuthGate(false)}
              className="mt-5 w-full text-center text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="auth-gate-cancel"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
